import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { Reveal } from "@/components/Reveal";
import { useSeo } from "@/lib/seo";
import { useLang, lp, loc } from "@/lib/i18n";

const T = {
  fr: {
    notFound: "Référence introuvable.", back: "Retour", backTop: "← Dames",
    format: "Eau de parfum · 100 ml · Série limitée numérotée",
    pyramid: "Pyramide", top: "Tête", heart: "Cœur", base: "Fond",
    unpublished: "La composition sera dévoilée au lancement.",
    perfumer: "Parfumeur : non communiqué. Seule l'œuvre signe.",
    soon: "Prochainement", notify: "Être averti du lancement",
    emailPlaceholder: "Ton adresse e-mail", notifyBtn: "M'avertir",
    seoDesc: (p, coup) => `DAMES ${p.name}, ${coup}. Eau de parfum concentrée, 100 ml, série limitée numérotée. Prochainement.`,
    alt: (p, coup) => `Flacon DAMES ${p.name} — ${coup}`,
  },
  en: {
    notFound: "Reference not found.", back: "Back", backTop: "← Dames",
    format: "Eau de parfum · 100 ml · Numbered limited series",
    pyramid: "Pyramid", top: "Top", heart: "Heart", base: "Base",
    unpublished: "The composition will be revealed at launch.",
    perfumer: "Perfumer: undisclosed. Only the work signs.",
    soon: "Coming soon", notify: "Be notified at launch",
    emailPlaceholder: "Your e-mail address", notifyBtn: "Notify me",
    seoDesc: (p, coup) => `DAMES ${p.name}, ${coup}. Concentrated eau de parfum, 100 ml, numbered limited series. Coming soon.`,
    alt: (p, coup) => `DAMES ${p.name} bottle — ${coup}`,
  },
};

const NotifyForm = ({ productRef, t }) => {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const { data } = await api.post("/notify", { email, ref: productRef });
      toast.success(data.message);
      setEmail("");
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };
  return (
    <form onSubmit={submit} className="mt-8 max-w-md" data-testid="notify-form">
      <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-400">{t.notify}</p>
      <input type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
        placeholder={t.emailPlaceholder} className="val-input mt-4" data-testid="notify-email" />
      <button type="submit" disabled={busy} className="val-btn mt-6" data-testid="notify-submit">
        {busy ? "…" : t.notifyBtn}
      </button>
    </form>
  );
};

export default function DamesProduct() {
  const { slug } = useParams();
  const lang = useLang();
  const t = T[lang];
  const [p, setP] = useState(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    setP(null); setErr(false);
    api.get(`/products/dames-${slug}`).then((r) => setP(r.data)).catch(() => setErr(true));
  }, [slug]);

  const coup = p && loc(p, lang, "coup");
  const description = p && loc(p, lang, "description");
  const pyramid = p && loc(p, lang, "pyramid");

  useSeo(
    p ? `DAMES ${p.name} — Maison Partita` : "Dames · Maison Partita",
    p ? t.seoDesc(p, coup) : ""
  );

  if (err) return <div className="max-w-3xl mx-auto px-5 py-40 text-center text-zinc-400">{t.notFound} <Link to={lp(lang, "/dames")} className="val-link underline">{t.back}</Link></div>;
  if (!p) return <div className="py-40 text-center text-zinc-600 tracking-[0.2em] uppercase text-[13px]">…</div>;

  return (
    <div className="max-w-[1400px] mx-auto px-5 sm:px-10 pt-16 pb-24">
      <Link to={lp(lang, "/dames")} className="val-link text-[13px] tracking-[0.2em] uppercase" data-testid="back-link">{t.backTop}</Link>

      <div className="mt-12 grid gap-16 lg:grid-cols-2">
        <Reveal>
          <div className="overflow-hidden bg-zinc-900 border border-zinc-800">
            <img src={p.photo} alt={t.alt(p, coup)} width="928" height="1152" decoding="async"
              className="w-full h-auto" style={{ aspectRatio: "4/5", objectFit: "cover" }} />
          </div>
        </Reveal>

        <div className="lg:pt-6">
          <Reveal>
            <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{coup}</p>
            <h1 className="mt-3 text-3xl sm:text-4xl tracking-[0.16em] uppercase text-zinc-50">DAMES {p.name}</h1>

            <p className="mt-8 text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.format}</p>

            <p className="mt-10 text-[19px] leading-[1.85] text-zinc-300">{description}</p>

            {pyramid ? (
              <div className="mt-12 space-y-4">
                <p className="text-[13px] tracking-[0.24em] uppercase text-zinc-500">{t.pyramid}</p>
                <Row label={t.top} v={pyramid.tete} />
                <Row label={t.heart} v={pyramid.coeur} />
                <Row label={t.base} v={pyramid.fond} />
              </div>
            ) : (
              <p className="mt-8 text-zinc-500 italic">{t.unpublished}</p>
            )}

            <p className="mt-10 text-zinc-500">{t.perfumer}</p>

            <div className="mt-12 border-t border-zinc-900 pt-10" data-testid="product-soon">
              <p className="text-[13px] tracking-[0.2em] uppercase text-zinc-500">{t.soon}</p>
              <NotifyForm productRef={`DAMES ${p.name}`} t={t} />
            </div>
          </Reveal>
        </div>
      </div>
    </div>
  );
}

const Row = ({ label, v }) => (
  <div className="flex gap-6">
    <span className="w-16 shrink-0 text-[13px] tracking-[0.14em] uppercase text-zinc-600">{label}</span>
    <span className="text-zinc-300">{v}</span>
  </div>
);
